/**
 * Notification-driven connection action popups (the MEMBER flow).
 *
 * Members never touch the Network tab for these — they click a connection
 * notification in the bell and the app opens one of these popups:
 *
 *   • AcceptConnectionPopup  — a recipient accepts an incoming request and
 *     picks which org (or Personal) to connect as  (stage 2).
 *   • ConfirmConnectionPopup — the initiator confirms the org the recipient
 *     picked, declines, or declines-and-reopens for a re-pick  (stage 3).
 *
 * Rendered at the app level (App.tsx) as portals, so they apply the theme
 * class explicitly (they live outside any ThemedArea).
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Clock, X } from 'lucide-react'
import { useToast, useTheme, getThemeClass, useConfirmOptional } from '@papan/core-ui/ui'
import { orgConnectionsApi, blockEndsConnectionConfirm, type ReshareReferral } from '../client/index.js'
import { RecipientAccessPicker, type SharePermission } from './SharePermissionPicker.js'

interface TenantOption {
  id: string
  name: string
  type: string
}

/** Levenshtein edit distance (small strings — org names). */
function editDistance(a: string, b: string): number {
  const m = a.length, n = b.length
  if (m === 0) return n
  if (n === 0) return m
  let prev = Array.from({ length: n + 1 }, (_, i) => i)
  let cur = new Array<number>(n + 1)
  for (let i = 1; i <= m; i++) {
    cur[0] = i
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost)
    }
    [prev, cur] = [cur, prev]
  }
  return prev[n]
}

/** The org whose name is closest to what the initiator typed. Exact and
 *  substring matches win; otherwise the nearest by edit distance, but only
 *  if it's reasonably close (so an unrelated org isn't auto-selected).
 *  Returns null when nothing matches well — caller defaults to Personal. */
function nearestOrg(typed: string | null | undefined, orgs: TenantOption[]): TenantOption | null {
  const q = (typed ?? '').trim().toLowerCase()
  if (!q || orgs.length === 0) return null
  let best: TenantOption | null = null
  let bestScore = Infinity
  for (const o of orgs) {
    const name = o.name.trim().toLowerCase()
    if (name === q) return o
    const sub = name.includes(q) || q.includes(name)
    const score = sub ? Math.min(editDistance(q, name), 1) : editDistance(q, name)
    if (score < bestScore) { bestScore = score; best = o }
  }
  // Accept only a close-enough match (half the typed length, min 2).
  return bestScore <= Math.max(2, Math.floor(q.length / 2)) ? best : null
}

/** "Name <email>" when a display name is known, else just the email. */
function Actor({ name, email }: { name?: string | null; email: string }) {
  return name
    ? <><span className="font-medium">{name}</span> <span className="font-mono themed-text-muted">&lt;{email}&gt;</span></>
    : <span className="font-mono">{email}</span>
}

/** "Expires in 13 days" / "Expires in 4 hours" / "Expired". The pending
 *  request carries a 14-day TTL. */
function ExpiryLine({ expiresAt }: { expiresAt: string | null }) {
  if (!expiresAt) return null
  const ms = new Date(expiresAt).getTime() - Date.now()
  let text: string
  if (ms <= 0) text = 'This request has expired'
  else {
    const days = Math.floor(ms / 86_400_000)
    const hours = Math.floor(ms / 3_600_000)
    text = days >= 1
      ? `Expires in ${days} day${days === 1 ? '' : 's'}`
      : `Expires in ${hours} hour${hours === 1 ? '' : 's'}`
  }
  return (
    <p className={`text-[11px] mb-3 mt-5 flex items-center gap-1 ${ms <= 0 ? 'text-red-400' : 'themed-text-muted'}`}>
      <Clock size={11} /> {text} · {new Date(expiresAt).toLocaleDateString()}
    </p>
  )
}

/**
 * #848 — an Add-to-Drive request can't be given more than the link gives the person who
 * asked (the server caps it). Said once, under the access picker, when the link is View.
 */
function LinkCapHint() {
  return (
    <p className="text-[11px] themed-text-muted mt-1.5">
      This request came from a View link, so adding it can give View at most. To give Edit,
      share the folder with them directly.
    </p>
  )
}

/** #1145: a Confirm / Approve that established nothing (the recipient's side can't take it now) is
 *  shown as information, never as a success. Only those two actions pass it to `run`. */
const landedKind = (result: unknown): 'info' | 'success' => {
  const state = (result as { state?: string } | null)?.state
  return state && state !== 'established' ? 'info' : 'success'
}

/** The success line after an approval: what actually LANDED when it is lower than the
 *  level picked (the server caps a link request at the link's level, and any share at
 *  what the sharer may give). The link is named only when the request came from one; an
 *  org that already held the folder keeps that share as granted, and is told so. */
function landedMessage(result: unknown, picked: string, fallback: string, fromLink: boolean, orgName?: string | null): string {
  const r = result as { state?: string; grantedPermission?: string | null; existingShare?: boolean } | null
  // Not established (#1145): the recipient's side can't take it right now, so it went back to them
  // to accept (or, for a link request, was closed). Never "confirmed" for something that didn't land.
  if (r?.state === 'pending_recipient') return "It's back with the recipient to accept — it lands once they do."
  if (r?.state && r.state !== 'established') return "This couldn't be completed — the recipient can't receive it right now."
  const granted = r?.grantedPermission
  if (r?.existingShare) {
    // Only the LEVEL is said to be kept: the approval may still have put the folder back in
    // their Drive (a binned `.shared`), so never "nothing changed". No level when unknown.
    const level = granted === 'readwrite' || granted === 'admin' ? 'Edit' : granted === 'readcopy' ? 'View' : null
    const who = orgName ?? 'Their organization'
    return level
      ? `${who} already had this shared with them — their access stays at ${level}. Change it from Manage access.`
      : `${who} already had this shared with them — their access was kept. Change it from Manage access.`
  }
  if (granted !== 'readcopy' || picked === 'readcopy') return fallback
  return fromLink ? "Added at View — that's the most the link gives them." : 'Added at View — the most this share can give.'
}

function ModalShell({ title, subtitle, onClose, children }: {
  title: string
  /** Tight line directly under the title (e.g. "Sent to <email>"). */
  subtitle?: React.ReactNode
  onClose: () => void
  children: React.ReactNode
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} relative themed-bg-secondary border themed-border rounded-xl shadow-2xl w-[400px] max-w-[90vw] p-4`}>
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium themed-text">{title}</h3>
            <button onClick={onClose} className="p-1 rounded themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors">
              <X size={14} />
            </button>
          </div>
          {subtitle && <div className="text-[11px] themed-text-muted mt-0.5">{subtitle}</div>}
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}

export function AcceptConnectionPopup({ requestId, fromEmail, fromName, orgName, initiatorNote, viaReshare, shareDriven, referrerName, referrerEmail, declineTargetEmail, sharedName, sharedIsFolder, targetTenantId, myTenants, onClose, onDone }: {
  requestId: string
  fromEmail: string
  /** Initiator's display name (shown as "Name <email>"). */
  fromName?: string | null
  /** Org name the initiator typed in the composer (display context). */
  orgName?: string | null
  /** A note from the initiator — set when the request was reopened via
   *  decline-and-resend (what they want the recipient to change). */
  initiatorNote?: string | null
  /** RESHARE referral: the request reached this recipient because the referrer
   *  reshared the initiator's (owner's) content — frame it as a shared folder
   *  from the referrer, not a cold connection request from the owner. */
  viaReshare?: boolean
  /** Share-driven request (a folder rode along): folder-first copy — the
   *  connection forms behind the scenes. Connection-only requests (Network
   *  page) keep the connection framing. */
  shareDriven?: boolean
  referrerName?: string | null
  referrerEmail?: string | null
  /** Who Reject & block blocks (the request's decline target, from the notice). */
  declineTargetEmail?: string | null
  sharedName?: string | null
  /** Whether the shared TARGET is a folder — a single-FILE share says "file"
   *  in the heading/body (fresh-share UX). Absent (legacy) ⇒ folder wording. */
  sharedIsFolder?: boolean
  /** RESHARE: the org this reshare targets (the org the recipient holds the
   *  resharer connection through). Pre-selects the "Receive as" picker so the
   *  recipient lands on that org, not Personal — B connected them as an org. */
  targetTenantId?: string | null
  myTenants: TenantOption[]
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const orgs = myTenants.filter(t => t.type === 'organization')
  // Default the "Receive/Connect as" pick:
  //  - RESHARE with a resolved target org the recipient belongs to → that org
  //    (B connected them as an org; that's the natural landing identity).
  //  - else an org name was typed by the initiator → the nearest-matching org.
  //  - else (sent "to you" / as a Person) → Personal.
  const [pick, setPick] = useState<string>(() =>
    (targetTenantId && orgs.some(o => o.id === targetTenantId) ? targetTenantId : null)
    ?? nearestOrg(orgName, orgs)?.id
    ?? 'personal')
  const [busy, setBusy] = useState(false)
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  // Inline sub-step: 'main' (pick + connect) → 'reject' (reason + reject /
  // reject & block, with a Back button).
  const [step, setStep] = useState<'main' | 'reject'>('main')
  const [note, setNote] = useState('')
  useEffect(() => {
    orgConnectionsApi.listIncoming()
      .then(({ incoming }) => setExpiresAt(incoming.find(r => r.requestId === requestId)?.expiresAt ?? null))
      .catch(() => {})
  }, [requestId])

  const accept = async () => {
    setBusy(true)
    try {
      const r = await orgConnectionsApi.recipientPick(requestId, pick === 'personal' ? null : pick)
      const shareFirstToast = viaReshare || shareDriven
      if (r.contactNoAccess === 'all') {
        // #773: accepted, but the org already had it and this person can't open it yet.
        toast.info(`Your organization already has this ${sharedIsFolder === false ? 'file' : 'folder'}. Someone who manages it has been asked to give you access.`)
        onDone()
        return
      }
      if (r.contactNoAccess === 'some') {
        toast.info('Your organization already had some of these. Someone who manages them has been asked to give you access.')
      }
      toast.success(
        r.alreadyConnected ? 'Already connected — nothing more to do'
          : r.state === 'established' ? (shareFirstToast ? `${sharedIsFolder === false ? 'File' : 'Folder'} added to your Drive` : 'Connection established')
            // A reshare parks at pending_source_approval — the OWNER approves next,
            // not the sender.
            : viaReshare ? `Sent to ${fromName ?? 'the owner'} for approval`
              : 'Accepted — waiting for them to confirm',
      )
      onDone()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to accept')
    } finally {
      setBusy(false)
    }
  }
  const run = async (fn: () => Promise<unknown>, msg: string | ((r: unknown) => string)) => {
    setBusy(true)
    try { const r = await fn(); toast.success(typeof msg === 'function' ? msg(r) : msg); onDone() }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Action failed') }
    finally { setBusy(false) }
  }
  const back = () => { setStep('main'); setNote('') }
  const confirm = useConfirmOptional()
  // The reject step names who Reject & block blocks — asked LIVE (an approval since the notice was
  // sent can change it); the notice's own answer meanwhile.
  const [liveTarget, setLiveTarget] = useState<string | null>(null)
  useEffect(() => {
    if (step !== 'reject') return
    orgConnectionsApi.blockImpact({ requestId }).then((i) => setLiveTarget(i.declineTargetEmail ?? null)).catch(() => {})
  }, [step, requestId])
  // dw D6: blocking a sender you're CONNECTED to also disconnects you and removes the folders shared
  // between you — say so, and ask, before doing it.
  const rejectAndBlock = async () => {
    const impact = await orgConnectionsApi.blockImpact({ requestId }).catch(() => null)
    if (impact?.blockFromTenantName) {
      toast.error(`This was sent to ${impact.blockFromTenantName} — it can only be blocked from there (by someone who manages its connections). You can still reject it.`)
      return
    }
    if (impact?.ownOrg) {
      toast.error("You can't block an organization you belong to. You can still reject the request.")
      return
    }
    if (impact?.needsManager) {
      toast.error("Your organization is connected to them, so blocking would disconnect it — only an owner or admin can do that. You can still reject the request.")
      return
    }
    const warn = blockEndsConnectionConfirm(impact)
    if (warn && confirm && !(await confirm({ ...warn, type: 'danger' }))) return
    await run(() => orgConnectionsApi.recipientReject(requestId, note.trim() || null, true),
      impact?.endsConnection ? 'Rejected, blocked & disconnected' : 'Rejected & blocked')
  }

  // Who Reject & block blocks — the decline target (`declineNoticeTarget`, carried by a reshare's
  // notice: the resharer while the owner hasn't decided, the owner once approved); else the sender.
  // (A notice sent before the field existed was a pending referral's — its resharer.)
  const rejectFrom = liveTarget ?? declineTargetEmail ?? (viaReshare && referrerEmail ? referrerEmail : fromEmail)

  if (step === 'reject') {
    return (
      <ModalShell title={(viaReshare || shareDriven) ? `Decline shared ${sharedIsFolder === false ? 'file' : 'folder'}` : 'Reject connection'} subtitle={<>From <span className="font-mono">{rejectFrom}</span></>} onClose={onClose}>
        <p className="text-[11px] themed-text-muted mb-2">Optionally provide a reason to notify them</p>
        <textarea
          value={note} onChange={(e) => setNote(e.target.value)} rows={3} autoFocus
          placeholder="Reason (optional)"
          className="w-full themed-input border rounded px-2 py-1.5 text-sm outline-none resize-none mb-4"
        />
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button onClick={back} disabled={busy} className="px-3 py-1.5 text-sm themed-text-muted hover:themed-text transition-colors disabled:opacity-50">Back</button>
          <button onClick={() => void rejectAndBlock()} disabled={busy}
            className="px-2.5 py-1.5 text-sm text-red-400 hover:bg-red-500/15 rounded transition-colors disabled:opacity-50">
            Reject &amp; block
          </button>
          <button onClick={() => void run(() => orgConnectionsApi.recipientReject(requestId, note.trim() || null, false), (r) => (r as { alreadyConnected?: boolean })?.alreadyConnected ? 'Already connected' : 'Request rejected')} disabled={busy}
            className="px-3 py-1.5 text-sm font-semibold bg-red-600 hover:bg-red-500 text-white rounded transition-colors disabled:opacity-50">
            Reject
          </button>
        </div>
      </ModalShell>
    )
  }

  const ownerLabel = fromName ?? fromEmail
  const shareFirst = viaReshare || shareDriven
  return (
    <ModalShell title={shareFirst ? `${sharedIsFolder === false ? 'File' : 'Folder'} shared with you` : 'Connection request'} onClose={onClose}>
      {viaReshare ? (
        // Reshare referral: the recipient dealt with the REFERRER — lead with
        // them and the folder.
        <p className="text-sm themed-text mb-1">
          {referrerEmail
            ? <Actor name={referrerName ?? null} email={referrerEmail} />
            : <span className="font-medium">{referrerName ?? 'Someone'}</span>} shared{' '}
          <span className="font-medium text-blue-300">{ownerLabel}</span>'s{' '}
          {sharedName ? <>“<span className="font-medium text-blue-400">{sharedName}</span>”</> : <>{sharedIsFolder === false ? 'file' : 'folder'}</>} with you
          — accept to receive it.
        </p>
      ) : shareDriven ? (
        // Share-driven first contact: the folder is the subject; the
        // connection is plumbing that forms behind the scenes.
        <p className="text-sm themed-text mb-1">
          <Actor name={fromName} email={fromEmail} /> shared{' '}
          {sharedName ? <>“<span className="font-medium text-blue-400">{sharedName}</span>”</> : <>a {sharedIsFolder === false ? 'file' : 'folder'}</>} with you
          — accept to receive it.
        </p>
      ) : (
        <p className="text-sm themed-text mb-1">
          <Actor name={fromName} email={fromEmail} /> wants to connect
          {orgName ? <> with <span className="font-medium">{orgName}</span></> : <> with you</>}.
        </p>
      )}
      {initiatorNote && (
        <p className="text-xs themed-text-muted mb-1 border-l-2 themed-border pl-2 italic">
          “{initiatorNote}”
        </p>
      )}
      <ExpiryLine expiresAt={expiresAt} />
      <label className="text-xs themed-text-muted block mb-1">{shareFirst ? 'Receive as' : 'Connect as'}</label>
      <select
        value={pick}
        onChange={(e) => setPick(e.target.value)}
        className="themed-input border rounded px-2 py-1.5 text-sm w-full mb-1.5 outline-none"
      >
        <option value="personal">Personal</option>
        {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
      {/* Explain what happens next. A RESHARE (viaReshare) always waits on the
          OWNER's approval — BOTH identities, not the sender — so the instant-vs-
          bounce framing below is wrong for it; give one accurate line instead. A
          direct share: a personal accept establishes instantly, an org accept
          routes back to the sender to confirm. */}
      <p className="text-[11px] themed-text-muted mb-4 leading-snug">
        {viaReshare
          ? `${ownerLabel} will approve this before ${sharedName ? `“${sharedName}”` : 'the folder'} is added to your Drive.`
          : pick === 'personal'
            ? (shareFirst ? 'Accepting as Personal adds it to your Drive instantly.' : 'Accepting as Personal connects you instantly.')
            : `Accepting as ${orgs.find(o => o.id === pick)?.name ?? 'this organization'} sends it back to the sender to confirm before it goes live.`}
      </p>
      <div className="flex items-center gap-2 flex-wrap justify-end">
        <button onClick={() => setStep('reject')} disabled={busy}
          className="px-3 py-1.5 text-sm text-red-400 hover:bg-red-500/15 rounded transition-colors disabled:opacity-50">
          {shareFirst ? 'Decline' : 'Reject'}
        </button>
        <button onClick={() => void accept()} disabled={busy}
          className="px-3 py-1.5 text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors disabled:opacity-50">
          {shareFirst ? 'Accept' : 'Connect'}
        </button>
      </div>
    </ModalShell>
  )
}

export function ConfirmConnectionPopup({ requestId, asTenantId, asOrgEmail, recipientEmail, recipientName, pickedTenantName, notifiedEmail, initiatorOrgName, initiatorIsPersonal, source, linkPermission, linkCap: linkCapIn, shareDriven, sharedName, onClose, onDone }: {
  requestId: string
  /** The request's INITIATOR tenant — confirm AS this, regardless of the
   *  caller's active tenant. The bell shows notifications across tenants, so
   *  this request may belong to a tenant that isn't currently active. */
  asTenantId?: string
  /** An owner/admin email of `asTenantId` — sent alongside as an identity
   *  override so a cross-tenant confirm (incl. a share_link_mount access
   *  override, gated on canManageConnection) resolves the right identity
   *  instead of the caller's active org-email. */
  asOrgEmail?: string | null
  recipientEmail: string
  /** Accepter's display name (shown as "Name <email>"). */
  recipientName?: string | null
  pickedTenantName: string | null
  /** The identity this notification was addressed to (shown as a subline). */
  notifiedEmail?: string | null
  /** The initiator's own org/tenant name ("connected your organization (…)"). */
  initiatorOrgName?: string | null
  /** The initiator (current user) is on a PERSONAL tenant — say "connected you"
   *  instead of "your organization (…)", which is wrong for a personal account. */
  initiatorIsPersonal?: boolean
  /** Distinguishes the request's origin. Defaults to org-connections
   *  semantics ("connected your organization"). For share-link mounts,
   *  the popup uses recipient-Drive wording instead — same Confirm/Decline,
   *  different copy. */
  source?: 'org_connection' | 'share_link_mount'
  /** The request's granted permission ('readcopy' | 'readwrite'), derived from
   *  the share link. For `share_link_mount` it seeds the approval-dialog access
   *  picker (the admin can override before confirming). */
  linkPermission?: string | null
  /** #848 — the most the link gives the person who asked (the request's asked level);
   *  caps the access picker. Not `linkPermission`: an owner may have lowered the
   *  request, and may raise it back. Absent ⇒ `linkPermission`. */
  linkCap?: string | null
  /** Share-driven request (a folder rode along): folder-first copy — confirm
   *  a SHARE, not a connection. Connection-only requests keep the old copy. */
  shareDriven?: boolean
  sharedName?: string | null
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  // Add-to-Drive approval: the admin can dial the recipient's access here
  // before confirming. Defaults — permission: the link's own permission;
  // re-sharing: off (the recipient must be explicitly granted onward sharing).
  // #848 — what the link gives the person who asked: the most this approval can give.
  // (Shown only — the server caps it. Read when the list was fetched: a takeover by a
  // lower asker, or the link lowering the asker, since then isn't reflected here — the
  // success line then says what landed. A live read would need the link's own tenant scope.)
  const linkCap: SharePermission = (linkCapIn ?? linkPermission) === 'readwrite' ? 'readwrite' : 'readcopy'
  // Seeded from the request's level, never above the cap (the request may have been raised).
  const [grantPermission, setGrantPermission] = useState<SharePermission>(
    linkPermission === 'readwrite' && linkCap === 'readwrite' ? 'readwrite' : 'readcopy',
  )
  const [allowSharing, setAllowSharing] = useState(false)
  // Inline sub-step: 'main' (confirm/decline/resend) → 'resend' (note to
  // send back) | 'decline' (reason). Both sub-steps have a Back button.
  const [step, setStep] = useState<'main' | 'resend' | 'decline'>('main')
  const [note, setNote] = useState('')
  useEffect(() => {
    // Read outgoing AS the request's own tenant/identity — a cross-tenant confirm
    // opens from a bell for another tenant, and an active-scoped read would miss
    // the request (empty expiry line).
    orgConnectionsApi.listOutgoing({ tenantId: asTenantId, orgEmail: asOrgEmail })
      .then(({ outgoing }) => setExpiresAt(outgoing.find(r => r.requestId === requestId)?.expiresAt ?? null))
      .catch(() => {})
  }, [requestId, asTenantId, asOrgEmail])
  const run = async (fn: () => Promise<unknown>, msg: string | ((r: unknown) => string), kind?: (r: unknown) => 'info' | 'success') => {
    setBusy(true)
    try {
      const r = await fn()
      ;(kind?.(r) === 'info' ? toast.info : toast.success)(typeof msg === 'function' ? msg(r) : msg)
      onDone()
    }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Action failed') }
    finally { setBusy(false) }
  }
  const back = () => { setStep('main'); setNote('') }
  // Shown on EVERY step's header so the header height stays constant (no
  // jump when switching main ↔ decline/resend).
  const subtitle = notifiedEmail ? <>Sent to <span className="font-mono">{notifiedEmail}</span></> : undefined

  // All steps share one min-height with the buttons pinned to the bottom,
  // so switching main ↔ decline/resend doesn't resize the modal.
  const stepBody = (content: React.ReactNode, actions: React.ReactNode) => (
    <div className="flex flex-col min-h-[150px]">
      <div className="flex-1">{content}</div>
      <div className="flex items-center gap-2 flex-wrap justify-end">{actions}</div>
    </div>
  )

  if (step === 'resend') {
    return (
      <ModalShell title="Decline & resend" subtitle={subtitle} onClose={onClose}>
        {stepBody(
          <>
            <p className="text-[11px] themed-text-muted mb-2">Add a note so they know what to pick instead</p>
            <textarea
              value={note} onChange={(e) => setNote(e.target.value)} rows={3} autoFocus
              placeholder="Reason (optional)"
              className="w-full themed-input border rounded px-2 py-1.5 text-sm outline-none resize-none"
            />
          </>,
          <>
            <button onClick={back} disabled={busy} className="px-3 py-1.5 text-sm themed-text-muted hover:themed-text transition-colors disabled:opacity-50">Back</button>
            <button onClick={() => void run(() => orgConnectionsApi.reject(requestId, note.trim() || null, true), 'Sent back — they can pick again')} disabled={busy}
              className="px-3 py-1.5 text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors disabled:opacity-50">
              Resend request
            </button>
          </>,
        )}
      </ModalShell>
    )
  }

  if (step === 'decline') {
    return (
      <ModalShell title={shareDriven ? 'Decline share' : 'Decline connection'} subtitle={subtitle} onClose={onClose}>
        {stepBody(
          <>
            <p className="text-[11px] themed-text-muted mb-2">Optionally provide a reason to notify them</p>
            <textarea
              value={note} onChange={(e) => setNote(e.target.value)} rows={3} autoFocus
              placeholder="Reason (optional)"
              className="w-full themed-input border rounded px-2 py-1.5 text-sm outline-none resize-none"
            />
          </>,
          <>
            <button onClick={back} disabled={busy} className="px-3 py-1.5 text-sm themed-text-muted hover:themed-text transition-colors disabled:opacity-50">Back</button>
            <button onClick={() => void run(() => orgConnectionsApi.reject(requestId, note.trim() || null, false), shareDriven ? 'Share declined' : 'Connection declined')} disabled={busy}
              className="px-3 py-1.5 text-sm font-semibold bg-red-600 hover:bg-red-500 text-white rounded transition-colors disabled:opacity-50">
              Decline
            </button>
          </>,
        )}
      </ModalShell>
    )
  }

  return (
    <ModalShell
      title={source === 'share_link_mount' ? 'Add-to-Drive request' : shareDriven ? 'Confirm your share' : 'Confirm connection'}
      subtitle={subtitle}
      onClose={onClose}
    >
      {stepBody(
        <>
          <p className="text-sm themed-text mb-1">
            <Actor name={recipientName} email={recipientEmail} />{' '}
            {source === 'share_link_mount' ? (
              // Share-link mount semantics: the recipient clicked
              // "Add to my Drive" on a share you opened. Confirming
              // creates the `.shared` mount in their drive.
              <>
                wants to add your shared folder
                {initiatorOrgName ? <> from <span className="font-medium text-blue-300">{initiatorOrgName}</span></> : null}
                {pickedTenantName ? <> to their <span className="font-medium text-blue-400">{pickedTenantName}</span> Drive.</> : <> to their Drive.</>}
              </>
            ) : shareDriven ? (
              // Share-driven: they accepted your FOLDER; the connection is
              // plumbing that forms behind the scenes on confirm.
              <>
                accepted {sharedName ? <>“<span className="font-medium">{sharedName}</span>”</> : <>your shared folder</>}
                {pickedTenantName ? <> as <span className="font-medium text-blue-400">{pickedTenantName}</span></> : null}
                {' '}— confirm to go live.
              </>
            ) : pickedTenantName ? (
              <>
                {initiatorIsPersonal ? (
                  <>connected you</>
                ) : (
                  <>connected your organization{initiatorOrgName ? <> <span className="font-medium text-blue-300">{initiatorOrgName}</span></> : null}</>
                )}
                {' '}to <span className="font-medium text-blue-400">{pickedTenantName}</span> per your request.
              </>
            ) : (
              <>connected you per your request.</>
            )}
          </p>
          {source === 'share_link_mount' && (
            // The admin sets the recipient's access at approval time. Seeded
            // from the link permission / no re-sharing; capped server-side at
            // the admin's own access on the folder.
            <div className="mt-3 flex items-center justify-between gap-3 rounded-md border themed-border px-3 py-2">
              <span className="text-xs themed-text-muted">Grant access</span>
              <RecipientAccessPicker
                permission={grantPermission}
                allowSharing={allowSharing}
                maxPermission={linkCap}
                onChange={(perm, share) => { setGrantPermission(perm); setAllowSharing(share) }}
              />
            </div>
          )}
          {source === 'share_link_mount' && linkCap === 'readcopy' && <LinkCapHint />}
          <ExpiryLine expiresAt={expiresAt} />
        </>,
        <>
          <button onClick={() => setStep('resend')} disabled={busy}
            className="px-2.5 py-1.5 text-xs themed-text-muted hover:themed-text border themed-border rounded transition-colors disabled:opacity-50">
            Decline &amp; resend
          </button>
          <button onClick={() => setStep('decline')} disabled={busy}
            className="px-2.5 py-1.5 text-xs text-red-400 hover:bg-red-500/15 rounded transition-colors disabled:opacity-50">
            Decline
          </button>
          <button onClick={() => void run(
            () => orgConnectionsApi.confirm(
              requestId,
              asTenantId,
              source === 'share_link_mount'
                ? { permission: grantPermission === 'admin' ? 'readwrite' : grantPermission, allowSharing }
                : undefined,
              asOrgEmail,
            ),
            (r) => landedMessage(r, grantPermission, shareDriven ? 'Share confirmed' : 'Connection confirmed', source === 'share_link_mount', pickedTenantName),
            landedKind,
          )} disabled={busy}
            className="px-3 py-1.5 text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors disabled:opacity-50">
            Confirm
          </button>
        </>,
      )}
    </ModalShell>
  )
}

/**
 * ReshareApprovalPopup — the SOURCE owner approves (or declines) an onward
 * reshare of their content. Opened from a `reshare_awaiting_source_approval`
 * notification. Approving creates a DIRECT owner→recipient share (the recipient
 * still accepts the handshake); declining closes the referral. Details
 * (folder name, referrer, recipient) are re-fetched from the owner inbox so a
 * stale notification can't drive a phantom approval.
 */
export function ReshareApprovalPopup({ requestId, referrerName, recipientEmail, asTenantId, asOrgEmail, onClose, onDone }: {
  requestId: string
  /** Referrer org name from the notification (fallback while details load). */
  referrerName?: string | null
  recipientEmail?: string | null
  /** Identity override — approve as an owner/admin of the owner tenant even when
   *  it isn't the active one (cross-tenant approve straight from the bell, no
   *  switch). Threads into the detail fetch AND the approve/reject calls. */
  asTenantId?: string | null
  asOrgEmail?: string | null
  onClose: () => void
  onDone: () => void
}) {
  const as = { tenantId: asTenantId, orgEmail: asOrgEmail }
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [step, setStep] = useState<'main' | 'decline'>('main')
  const [note, setNote] = useState('')
  const [detail, setDetail] = useState<ReshareReferral | null>(null)
  const [loaded, setLoaded] = useState(false)
  // The SOURCE OWNER sets the recipient's access here — the only place a reshare
  // can be lifted to read-write (the resharer's proposal was capped at read-only).
  // Seed from the referral's proposed level once it loads; re-sharing defaults off.
  const [grantPermission, setGrantPermission] = useState<SharePermission>('readcopy')
  const [allowSharing, setAllowSharing] = useState(false)
  useEffect(() => {
    // Read the owner tenant's inbox — via the identity override when approving a
    // notification for another tenant, so `detail` resolves cross-tenant.
    orgConnectionsApi.listReshareReferrals(as)
      .then(({ referrals }) => setDetail(referrals.find(r => r.requestId === requestId) ?? null))
      .catch(() => {})
      .finally(() => setLoaded(true))
  }, [requestId, asTenantId, asOrgEmail]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (detail?.permission) setGrantPermission(detail.permission === 'readwrite' || detail.permission === 'admin' ? 'readwrite' : 'readcopy')
  }, [detail?.permission])

  const run = async (fn: () => Promise<unknown>, msg: string | ((r: unknown) => string), kind?: (r: unknown) => 'info' | 'success') => {
    setBusy(true)
    try {
      const r = await fn()
      ;(kind?.(r) === 'info' ? toast.info : toast.success)(typeof msg === 'function' ? msg(r) : msg)
      onDone()
    }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Action failed') }
    finally { setBusy(false) }
  }
  const back = () => { setStep('main'); setNote('') }

  const folder = detail?.sourceFolderName ?? 'your content'
  const referrer = detail?.referrerOrgName ?? referrerName ?? 'Another organization'
  // Who the referrer actually is — shown on hover, since `referrer` is an org
  // name on its own and says nothing about the person behind it.
  const referrerEmail = detail?.referrerEmail ?? null
  const recipient = detail?.recipientEmail ?? recipientEmail ?? 'someone'
  const recipientName = detail?.recipientName ?? null
  // The recipient already chose their receiving identity (Option 2) — show it so
  // the owner approves the concrete destination, not a bare email.
  const recipientOrg = detail?.recipientOrgName ?? null
  const isFolder = detail?.sourceIsFolder ?? true

  const stepBody = (content: React.ReactNode, actions: React.ReactNode) => (
    <div className="flex flex-col min-h-[150px]">
      <div className="flex-1">{content}</div>
      <div className="flex items-center gap-2 flex-wrap justify-end">{actions}</div>
    </div>
  )

  // Loaded but gone from the inbox ⇒ already approved/declined/expired.
  if (loaded && !detail) {
    return (
      <ModalShell title="Reshare request" onClose={onClose}>
        {stepBody(
          <p className="text-sm themed-text-muted">This reshare request is no longer awaiting your approval.</p>,
          <button onClick={onClose} className="px-3 py-1.5 text-sm themed-text-muted hover:themed-text transition-colors">Close</button>,
        )}
      </ModalShell>
    )
  }

  if (step === 'decline') {
    return (
      <ModalShell title="Decline reshare" onClose={onClose}>
        {stepBody(
          <>
            <p className="text-[11px] themed-text-muted mb-2">Optionally add a reason</p>
            <textarea
              value={note} onChange={(e) => setNote(e.target.value)} rows={3} autoFocus
              placeholder="Reason (optional)"
              className="w-full themed-input border rounded px-2 py-1.5 text-sm outline-none resize-none"
            />
          </>,
          <>
            <button onClick={back} disabled={busy} className="px-3 py-1.5 text-sm themed-text-muted hover:themed-text transition-colors disabled:opacity-50">Back</button>
            <button onClick={() => void run(() => orgConnectionsApi.sourceReject(requestId, note.trim() || null, as), 'Reshare declined')} disabled={busy}
              className="px-3 py-1.5 text-sm font-semibold bg-red-600 hover:bg-red-500 text-white rounded transition-colors disabled:opacity-50">
              Decline
            </button>
          </>,
        )}
      </ModalShell>
    )
  }

  return (
    <ModalShell title={isFolder ? 'Share your folder?' : 'Share your file?'} onClose={onClose}>
      {stepBody(
        <>
          <p className="text-sm themed-text mb-1">
            <span className="font-medium text-blue-300" title={referrerEmail ?? undefined}>{referrer}</span> wants to share your{' '}
            {isFolder ? 'folder' : 'file'} <span className="font-medium text-blue-400">{folder}</span> with{' '}
            <Actor name={recipientName} email={recipient} />
            {recipientOrg ? <> — receiving as <span className="font-medium text-blue-400">{recipientOrg}</span></> : null}.
          </p>
          {/* The owner sets the recipient's access — only the owner can grant
              read-write on a reshare. Seeded from the proposed level; capped
              server-side at the owner's own access on the folder. */}
          <div className="mt-3 flex items-center justify-between gap-3 rounded-md border themed-border px-3 py-2">
            <span className="text-xs themed-text-muted">Grant access</span>
            <RecipientAccessPicker
              permission={grantPermission}
              allowSharing={allowSharing}
              maxPermission={detail?.fromShareLink ? 'readcopy' : undefined}
              onChange={(perm, share) => { setGrantPermission(perm); setAllowSharing(share) }}
            />
          </div>
          {/* #848 — a re-share made from a link (Add to Drive) gives at most the link's View. */}
          {detail?.fromShareLink && <LinkCapHint />}
          <p className="text-[11px] themed-text-muted mt-3">
            Approving creates a direct share from you to <span className="font-mono">{recipient}</span> — you stay in control and can revoke it anytime.
          </p>
        </>,
        <>
          <button onClick={() => setStep('decline')} disabled={busy}
            className="px-2.5 py-1.5 text-xs text-red-400 hover:bg-red-500/15 rounded transition-colors disabled:opacity-50">
            Decline
          </button>
          <button onClick={() => void run(
            () => orgConnectionsApi.sourceApprove(requestId, {
              permission: grantPermission === 'admin' ? 'readwrite' : grantPermission,
              allowSharing,
            }, as),
            (r) => landedMessage(r, grantPermission, 'Reshare approved', detail?.fromShareLink === true, detail?.recipientOrgName),
            landedKind,
          )} disabled={busy}
            className="px-3 py-1.5 text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors disabled:opacity-50">
            Approve
          </button>
        </>,
      )}
    </ModalShell>
  )
}
